/**
 * Подтверждение на действии (docs/API.md §2).
 *
 * Обязательного второго фактора на входе в продукте нет — в инструменте, куда
 * заходят раз в неделю, он даёт предсказуемый результат: общий аккаунт на отдел
 * и пароль в переписке. Вместо него подтверждается **действие**: оплата,
 * приглашение и удаление участника, смена роли, смена почты, отмена подписки.
 *
 * Механика ровно такая, как её описывает контракт, и разложена она так, чтобы
 * экран о ней почти не знал:
 *
 * 1. экран делает своё действие как обычно;
 * 2. сервер отвечает `403 step_up_required` и кладёт в проблему `action`;
 * 3. здесь открывается окно, человек вводит пароль или код приложения;
 * 4. `POST /auth/step-up/{id}/confirm` отдаёт токен на десять минут;
 * 5. действие повторяется с заголовком `X-Seeno-Step-Up`.
 *
 * Токен живёт в памяти вкладки и переиспользуется до истечения: спрашивать
 * пароль на каждую строку списка участников — верный способ научить человека
 * вводить его не глядя.
 *
 * **Переиспользуется он только внутри того же действия.** Контракт выдаёт
 * запрос под конкретный `action` (`StepUpRequest.action` обязателен), и токен
 * почти наверняка привязан к действию на сервере. Общий кэш «один токен на
 * всё» давал такую картину: человек подтвердил оплату, через минуту нажал
 * «Отозвать доступ», кэш отдал чужой токен, сервер ответил `step_up_required`
 * — и окно подтверждения больше не открывалось, потому что кэш никто не
 * сбрасывал. Функция была мертва до перезагрузки вкладки на все оставшиеся
 * девять минут.
 *
 * ─── Названия действий ──────────────────────────────────────────────────────
 *
 * Русское название действия живёт в каталоге строк под ключом
 * `step-up.action.<код>`, где код обязан СЛОВО В СЛОВО повторять реестр
 * сервера (`src/seeno_api/platform/stepup_registry.py`): имя приезжает в теле
 * ответа `POST /auth/step-up`, а не выбирается здесь. Пока карта жила своей
 * жизнью — `billing.checkout`, `billing.cancel`, `billing.payment_method`, —
 * она не совпадала ни с одним кодом сервера, и во ВСЕХ денежных подтверждениях
 * подставлялась заглушка. Полнота проверяется приёмкой
 * `tools/check-step-up.mjs`: код без названия возвращает заглушку, то есть
 * дефект молча воспроизводится.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

import { Dialog } from './Dialog';
import { Field } from './form';
import { ErrorNotice } from './state';
import { hasMessage, t } from '../i18n';
import { api } from '../api/client';
import { ApiError, extra } from '../api/problem';
import type { components } from '../api/schema';

type Challenge = components['schemas']['StepUpChallenge'];
type StepUpToken = components['schemas']['StepUpToken'];

/** Человек закрыл окно. Это не ошибка — экран просто ничего не делает. */
export class StepUpCancelled extends Error {
  constructor() {
    super('Подтверждение отменено');
    this.name = 'StepUpCancelled';
  }
}

/**
 * Спросить подтверждение под конкретное действие, либо забыть выданное.
 *
 * `forget` нужен вызывающему: только он видит, что сервер отказал повтору с
 * токеном, и только он может сказать, что держать этот токен дальше незачем.
 */
interface StepUp {
  ask: (action: string) => Promise<string>;
  forget: () => void;
}

const Context = createContext<StepUp | null>(null);

interface Pending {
  action: string;
  challenge: Challenge | null;
  /**
   * Почему запроса нет, если его нет.
   *
   * Отказ выдачи ЗАПРЕЩЕНО терять. Здесь стоял `.catch(() => null)`, и вместе с
   * причиной пропадал единственный текст, который человеку помогал: сервер
   * отвечает учётной записи без пароля «Подтверждать действие нечем: задайте
   * пароль в настройках», а окно вместо этого сочиняло «Подтверждение сейчас
   * недоступно: сервис не выдал запрос. Повторите действие через минуту».
   * Повтор через минуту не помогает никогда — состояние само не меняется, и
   * человек упирался в тупик прямо на экране оплаты.
   */
  problem: unknown;
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Выданный токен, действие, под которое он выдан, и момент, когда он
  // перестаёт годиться. Живёт только в памяти вкладки: в хранилище это
  // подтверждение, пережившее закрытие браузера.
  const token = useRef<{ action: string; value: string; until: number } | null>(null);

  const forget = useCallback(() => {
    token.current = null;
  }, []);

  const ask = useCallback(async (action: string) => {
    const held = token.current;
    // Совпадение действия — обязательное условие. Токен от `billing.checkout`,
    // отданный в ответ на `members.remove`, сервер не примет, а экран получит
    // отказ вместо окна подтверждения.
    if (held && held.action === action && held.until > Date.now() + 5000) {
      return held.value;
    }

    let challenge: Challenge | null = null;
    let problem: unknown = null;
    try {
      challenge = await api.post<Challenge>('/auth/step-up', { action });
    } catch (caught) {
      problem = caught;
    }

    return new Promise<string>((resolve, reject) => {
      setSecret('');
      setError(null);
      setPending({ action, challenge, problem, resolve, reject });
    });
  }, []);

  const stepUp = useMemo<StepUp>(() => ({ ask, forget }), [ask, forget]);

  function close() {
    pending?.reject(new StepUpCancelled());
    setPending(null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!pending?.challenge) return;
    setBusy(true);
    setError(null);
    try {
      // Шесть цифр — код приложения, всё остальное считаем паролем. Отдельного
      // переключателя нет: он заставляет думать о механике, а не о действии.
      const digits = /^[0-9]{6}$/.test(secret.trim());
      const result = await api.post<StepUpToken>(
        `/auth/step-up/${encodeURIComponent(pending.challenge.challenge_id)}/confirm`,
        digits ? { totp_code: secret.trim() } : { password: secret },
      );
      token.current = {
        action: pending.action,
        value: result.step_up_token,
        until: new Date(result.expires_at).getTime(),
      };
      pending.resolve(result.step_up_token);
      setPending(null);
      setSecret('');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  // Название действия либо есть, либо подставляется общее слово: подстановка
  // «это действие» давала «подтвердить это действие — это действие меняет…».
  const named = pending ? hasMessage(`step-up.action.${pending.action}`) : false;
  const what = pending && named ? t(`step-up.action.${pending.action}`) : '';
  const totp = pending?.challenge?.methods.includes('totp') ?? false;

  return (
    <Context.Provider value={stepUp}>
      {children}

      {/* Окно рисуется, только когда есть что подтверждать.
          Раньше `<dialog>` со всем содержимым висел в разметке ВСЕГДА — в том
          числе на публичных `/auth/signup` и `/audit`, куда подтверждение не
          приходит и прийти не может, — и нёс фразу с пустой подстановкой:
          «Мы просим подтвердить  — это действие меняет доступ или деньги».
          Скрытый элемент остаётся в дереве доступности инструментов и в
          исходнике страницы: то, что его не видно, не делает его отсутствующим. */}
      {pending === null ? null : (
        <Dialog
          open
          title={t('step-up.title')}
          onClose={close}
          footer={
            <>
              <button className="btn btn-ghost btn-sm" type="button" onClick={close}>
                {t('step-up.cancel')}
              </button>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                form="step-up-form"
                disabled={busy || !pending?.challenge}
              >
                {busy ? t('step-up.checking') : t('step-up.confirm')}
              </button>
            </>
          }
        >
          {/* Обещания «второй раз за десять минут не спросим» здесь больше нет, и
            вернуться оно не может: подтверждение одноразовое. Сервер гасит его
            в том же запросе, которым проверяет (`consumed_at` в
            `read_satisfied_step_up`), поэтому следующее денежное действие
            спрашивает снова — хоть через две минуты, хоть через час. Обещание
            было неправдой ровно с первого дня. */}
          <p className="dlg-lead">
            {t('step-up.lead', { what: named ? what : t('step-up.action-generic') })}
          </p>

          {error ? <ErrorNotice error={error} /> : null}

          {pending && !pending.challenge ? (
            /* Причина отказа приходит от сервера словами и показывается как есть:
             он единственный знает, чего именно не хватает. Сочинять здесь свой
             текст нельзя — предыдущий сочинённый («повторите через минуту») был
             прямой неправдой: состояние само не меняется, и повтор не помогал
             ни разу. Рядом стоит действие, которое действительно работает. */
            <ErrorNotice
              error={pending.problem}
              action={
                <a className="btn btn-ghost btn-sm" href="/settings">
                  {t('step-up.open-settings')}
                </a>
              }
            />
          ) : (
            <form id="step-up-form" onSubmit={submit} noValidate>
              <Field
                id="step-up-secret"
                label={totp ? t('step-up.secret.label-totp') : t('step-up.secret.label')}
                hint={totp ? t('step-up.secret.hint-totp') : t('step-up.secret.hint')}
              >
                {(props) => (
                  <input
                    {...props}
                    type="password"
                    autoComplete="current-password"
                    required
                    value={secret}
                    ref={(node) => node?.focus()}
                    onChange={(event) => setSecret(event.target.value)}
                  />
                )}
              </Field>
            </form>
          )}
        </Dialog>
      )}
    </Context.Provider>
  );
}

/**
 * Действие, которое умеет подтвердиться.
 *
 * Экран пишет обычный вызов и не думает о заголовках: если сервер попросил
 * подтверждение, оно спрашивается и вызов повторяется тем же самым кодом.
 * Ключ идемпотентности при повторе обязан быть **тем же** — иначе повтор
 * создаёт второе приглашение или второй платёж, поэтому его формирует
 * вызывающий и передаёт в замыкание один раз.
 */
export function useConfirmed(): <T>(
  action: string,
  call: (stepUpToken?: string) => Promise<T>,
) => Promise<T> {
  const stepUp = useContext(Context);
  if (!stepUp) throw new Error('StepUpProvider не найден выше по дереву');

  return useMemo(
    () =>
      async function confirmed<T>(action: string, call: (t?: string) => Promise<T>) {
        const needsStepUp = (caught: unknown) =>
          caught instanceof ApiError && caught.code === 'step_up_required';

        try {
          return await call();
        } catch (caught) {
          if (!needsStepUp(caught)) throw caught;
          // Сервер знает точнее, что именно подтверждают: имя действия приходит
          // в теле проблемы.
          const what = extra((caught as ApiError).problem, 'action') ?? action;

          // Запрос подтверждения берётся ДО `try`, и это принципиально. Пока
          // `ask` стоял внутри, его собственный отказ разбирался как отказ
          // повтора: сервер отвечал на «выдай подтверждение» кодом
          // `step_up_required`, ветка ниже читала его как «спроси
          // подтверждение» и спрашивала ещё раз — то же самое. Учётная запись
          // без пароля крутилась по этому кольцу и упиралась в тупик на экране
          // оплаты. Отказ выдачи обязан долетать до вызывающего как есть.
          const first = await stepUp.ask(what);

          try {
            return await call(first);
          } catch (again) {
            if (!needsStepUp(again)) throw again;
            // Повтор с токеном снова упёрся в подтверждение: токен не годится —
            // протух между выдачей и повтором или выдан не под то действие.
            // Держать его дальше нельзя, иначе он будет отдаваться из кэша до
            // самого истечения и действие останется мёртвым. Выбрасываем и
            // спрашиваем заново — **один раз**, чтобы не зациклиться на
            // сервере, который отвечает `step_up_required` всегда.
            stepUp.forget();
            return call(await stepUp.ask(what));
          }
        }
      },
    [stepUp],
  );
}
